import React, { useState, useEffect } from "react";
import { CareerPlanPDF, CareerPlanPDFProps } from "./CareerPlanPDF";
import activityIcon from "@/assets/icons/ui/activity-icon.svg";
import DOMPurify from "dompurify";

/**
 * Optimized SVG to PNG converter with caching
 * Only converts unique SVGs once and reuses the result
 */

// Cache for converted SVG URLs to avoid duplicate conversions
const svgConversionCache = new Map<string, Promise<string | undefined>>();

// Helper to check if a hostname is private
const isPrivateOrLocalhost = (hostname: string): boolean => {
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  ) {
    return true;
  }
  const privateIPRanges = [
    /^10\./,
    /^172\.(1[6-9]|2\d|3[01])\./,
    /^192\.168\./,
    /^169\.254\./,
    /^127\./,
    /^0\./,
  ];
  return privateIPRanges.some((regex) => regex.test(hostname));
};

// Validate URL for SSRF protection
const sanitizeUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const trimmed = url.trim();
    const parsedUrl = new URL(trimmed);
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return undefined;
    }
    if (isPrivateOrLocalhost(parsedUrl.hostname)) {
      return undefined;
    }
    return parsedUrl.href;
  } catch {
    return undefined;
  }
};

// Check if URL is SVG
const isSvgUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url.toLowerCase().includes(".svg") || url.toLowerCase().includes("svg")
  );
};

// Robust SVG sanitization using DOMPurify
// Prevents XSS attacks by using a battle-tested sanitization library
const sanitizeSvgContent = (svgText: string): string => {
  if (!svgText) return "";

  // Configure DOMPurify for SVG sanitization
  const cleanSvg = DOMPurify.sanitize(svgText, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: [
      "svg",
      "path",
      "circle",
      "rect",
      "line",
      "polyline",
      "polygon",
      "g",
      "defs",
      "clipPath",
      "mask",
    ],
    ADD_ATTR: [
      "viewBox",
      "xmlns",
      "fill",
      "stroke",
      "stroke-width",
      "transform",
      "d",
      "cx",
      "cy",
      "r",
      "x",
      "y",
      "width",
      "height",
      "points",
    ],
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input"],
    FORBID_ATTR: [
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onmouseout",
      "onanimationend",
      "onanimationstart",
      "ontransitionend",
    ],
  });

  return cleanSvg;
};

// Sanitize error messages for logging to prevent log injection
const sanitizeForLogging = (message: string): string => {
  if (!message) return "";

  // Remove/replace dangerous characters to prevent log injection:
  // - Control characters (ASCII 0-31) including newlines, carriage returns, tabs
  // - ANSI escape sequences that can manipulate terminal output
  // - Delete character (ASCII 127)
  // - Null bytes
  let sanitized = message
    .replace(/[\x00-\x1F\x7F]/g, " ") // Remove all control characters
    .replace(/\x1b\[[0-9;]*[a-zA-Z]/g, "") // Remove ANSI escape sequences
    .trim();

  // Truncate very long messages to prevent log overflow attacks
  const MAX_LOG_LENGTH = 1000;
  if (sanitized.length > MAX_LOG_LENGTH) {
    sanitized = sanitized.substring(0, MAX_LOG_LENGTH) + "... (truncated)";
  }

  return sanitized;
};

// Safely stringify error for logging
const sanitizeError = (error: unknown): string => {
  if (error instanceof Error) {
    const message = sanitizeForLogging(error.message);
    const stack = error.stack ? sanitizeForLogging(error.stack) : "";
    return `${message}${stack ? ` | Stack: ${stack}` : ""}`;
  }
  return sanitizeForLogging(String(error));
};

// Optimized SVG to PNG converter with caching
const convertSvgToPng = async (
  svgUrl: string,
  size: number = 24,
): Promise<string | undefined> => {
  // Create cache key
  const cacheKey = `${svgUrl}-${size}`;

  // Return cached promise if exists
  if (svgConversionCache.has(cacheKey)) {
    return svgConversionCache.get(cacheKey);
  }

  // Create new conversion promise
  const conversionPromise = (async () => {
    try {
      const sanitizedUrl = sanitizeUrl(svgUrl);
      if (!sanitizedUrl) return undefined;

      const response = await fetch(sanitizedUrl);
      if (!response.ok) return undefined;

      // Sanitize SVG content immediately to prevent XSS
      const rawSvgText = await response.text();
      const sanitizedSvgText = sanitizeSvgContent(rawSvgText);
      if (!sanitizedSvgText) return undefined;

      // Additional validation: ensure sanitized content is still valid SVG
      if (!sanitizedSvgText.includes("<svg")) {
        console.warn(
          "Sanitization removed SVG content - potential security issue",
        );
        return undefined;
      }

      // Additional validation: ensure no script-like content remains after sanitization
      const dangerousPatterns = [
        /<script/i,
        /javascript:/i,
        /on\w+\s*=/i, // event handlers like onclick=, onload=
        /<iframe/i,
        /<embed/i,
        /<object/i,
      ];
      if (dangerousPatterns.some((pattern) => pattern.test(sanitizedSvgText))) {
        console.warn("Dangerous content detected after sanitization");
        return undefined;
      }

      // Create canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return undefined;

      canvas.width = size;
      canvas.height = size;

      // Create image element with security timeout
      const img = new window.Image();
      const LOAD_TIMEOUT_MS = 5000; // 5 second timeout

      return new Promise<string | undefined>((resolve) => {
        let timeoutId: NodeJS.Timeout | null = null;

        const cleanup = () => {
          if (timeoutId) clearTimeout(timeoutId);
          img.onload = null;
          img.onerror = null;
        };

        img.onload = () => {
          try {
            cleanup();
            ctx.clearRect(0, 0, size, size);
            ctx.drawImage(img, 0, 0, size, size);
            resolve(canvas.toDataURL("image/png"));
          } catch {
            resolve(undefined);
          }
        };

        img.onerror = () => {
          cleanup();
          resolve(undefined);
        };

        // Set timeout for loading
        timeoutId = setTimeout(() => {
          cleanup();
          console.warn("SVG conversion timeout exceeded");
          resolve(undefined);
        }, LOAD_TIMEOUT_MS);

        // Use data URL with properly sanitized and encoded SVG
        // encodeURIComponent provides additional encoding protection
        const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sanitizedSvgText)}`;
        img.src = svgDataUrl;
      });
    } catch {
      return undefined;
    }
  })();

  // Cache the promise
  svgConversionCache.set(cacheKey, conversionPromise);
  return conversionPromise;
};

// Collect unique SVG URLs from data structure
export const collectUniqueSvgUrls = (data: any, cmsData: any): Set<string> => {
  const urls = new Set<string>();

  // Core types icons
  cmsData?.coreTypesCms?.forEach((core: any) => {
    if (isSvgUrl(core?.icon)) urls.add(core.icon);
  });

  // Work values icons
  cmsData?.workValuesCms?.forEach((work: any) => {
    if (isSvgUrl(work?.icon)) urls.add(work.icon);
  });

  // Education icons
  cmsData?.educationCms?.forEach((edu: any) => {
    if (isSvgUrl(edu?.displayIcon?.url)) urls.add(edu.displayIcon.url);
    if (isSvgUrl(edu?.image?.url)) urls.add(edu.image.url);
    if (isSvgUrl(edu?.icon?.url)) urls.add(edu.icon.url);
  });

  // Building blocks
  cmsData?.buildingBlocksCms?.forEach((block: any) => {
    if (isSvgUrl(block?.iconUrl)) urls.add(block.iconUrl);
    if (isSvgUrl(block?.image?.url)) urls.add(block.image.url);
  });

  // Building block activities
  data?.buildingBlocksActivities?.forEach((activity: any) => {
    if (isSvgUrl(activity?.details?.category?.icon?.url)) {
      urls.add(activity.details.category.icon.url);
    }
  });

  // Next steps
  data?.nextSteps?.forEach((step: any) => {
    if (isSvgUrl(step?.buildingBlock?.icon?.url)) {
      urls.add(step.buildingBlock.icon.url);
    }
  });

  // Activity icon
  urls.add(activityIcon);

  return urls;
};

// Convert only the unique SVGs in parallel
export const convertUniqueSvgs = async (
  urls: Set<string>,
): Promise<Map<string, string>> => {
  const conversions = new Map<string, string>();

  // Convert all unique URLs in parallel
  const results = await Promise.allSettled(
    Array.from(urls).map(async (url) => {
      const png = await convertSvgToPng(url, 24);
      return { url, png };
    }),
  );

  // Store successful conversions
  results.forEach((result) => {
    if (result.status === "fulfilled" && result.value.png) {
      conversions.set(result.value.url, result.value.png);
    }
  });

  return conversions;
};

// Apply conversions to data structure
export const applyConversions = (
  data: any,
  cmsData: any,
  conversions: Map<string, string>,
) => {
  const convertedCmsData = cmsData ? { ...cmsData } : undefined;
  const convertedData = { ...data };

  if (convertedCmsData?.coreTypesCms) {
    convertedCmsData.coreTypesCms = convertedCmsData.coreTypesCms.map(
      (core: any) => {
        const converted = { ...core };
        if (isSvgUrl(converted.icon) && conversions.has(converted.icon)) {
          converted.icon = conversions.get(converted.icon);
        }
        return converted;
      },
    );
  }

  if (convertedCmsData?.workValuesCms) {
    convertedCmsData.workValuesCms = convertedCmsData.workValuesCms.map(
      (work: any) => {
        const converted = { ...work };
        if (isSvgUrl(converted.icon) && conversions.has(converted.icon)) {
          converted.icon = conversions.get(converted.icon);
        }
        return converted;
      },
    );
  }

  if (convertedCmsData?.educationCms) {
    convertedCmsData.educationCms = convertedCmsData.educationCms.map(
      (edu: any) => {
        const converted = { ...edu };
        if (
          isSvgUrl(converted.displayIcon?.url) &&
          conversions.has(converted.displayIcon.url)
        ) {
          converted.displayIcon = {
            ...converted.displayIcon,
            url: conversions.get(converted.displayIcon.url),
          };
        }
        if (
          isSvgUrl(converted.image?.url) &&
          conversions.has(converted.image.url)
        ) {
          converted.image = {
            ...converted.image,
            url: conversions.get(converted.image.url),
          };
        }
        if (
          isSvgUrl(converted.icon?.url) &&
          conversions.has(converted.icon.url)
        ) {
          converted.icon = {
            ...converted.icon,
            url: conversions.get(converted.icon.url),
          };
        }
        return converted;
      },
    );
  }

  if (convertedCmsData?.buildingBlocksCms) {
    convertedCmsData.buildingBlocksCms = convertedCmsData.buildingBlocksCms.map(
      (block: any) => {
        const converted = { ...block };
        if (isSvgUrl(converted.iconUrl) && conversions.has(converted.iconUrl)) {
          converted.iconUrl = conversions.get(converted.iconUrl);
        }
        if (
          isSvgUrl(converted.image?.url) &&
          conversions.has(converted.image.url)
        ) {
          converted.image = {
            ...converted.image,
            url: conversions.get(converted.image.url),
          };
        }
        return converted;
      },
    );
  }

  if (convertedData.buildingBlocksActivities) {
    convertedData.buildingBlocksActivities =
      convertedData.buildingBlocksActivities.map((activity: any) => {
        const converted = { ...activity };
        if (
          isSvgUrl(converted.details?.category?.icon?.url) &&
          conversions.has(converted.details.category.icon.url)
        ) {
          converted.details = {
            ...converted.details,
            category: {
              ...converted.details.category,
              icon: {
                ...converted.details.category.icon,
                url: conversions.get(converted.details.category.icon.url),
              },
            },
          };
        }
        return converted;
      });
  }

  if (convertedData.nextSteps) {
    convertedData.nextSteps = convertedData.nextSteps.map((step: any) => {
      const converted = { ...step };
      if (
        isSvgUrl(converted.buildingBlock?.icon?.url) &&
        conversions.has(converted.buildingBlock.icon.url)
      ) {
        converted.buildingBlock = {
          ...converted.buildingBlock,
          icon: {
            ...converted.buildingBlock.icon,
            url: conversions.get(converted.buildingBlock.icon.url),
          },
        };
      }
      return converted;
    });
  }

  return {
    convertedData,
    convertedCmsData,
    activityIconPng: conversions.get(activityIcon),
  };
};

export const CareerPlanPDFWithSvgConversion: React.FC<CareerPlanPDFProps> = (
  props,
) => {
  const [convertedCmsData, setConvertedCmsData] = useState(props.cmsData);
  const [convertedData, setConvertedData] = useState(props.data);
  const [activityIconPng, setActivityIconPng] = useState<string | undefined>();
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    const convertImages = async () => {
      setIsConverting(true);
      try {
        // Collect unique SVG URLs
        const uniqueUrls = collectUniqueSvgUrls(props.data, props.cmsData);
        console.log(
          sanitizeForLogging(
            `[PDF] Found ${uniqueUrls.size} unique SVG URLs to convert`,
          ),
        );

        // Convert all unique SVGs in parallel
        const conversions = await convertUniqueSvgs(uniqueUrls);
        console.log(
          sanitizeForLogging(
            `[PDF] Successfully converted ${conversions.size} SVGs to PNG`,
          ),
        );

        // Apply conversions to data structures
        const {
          convertedData: newData,
          convertedCmsData: newCmsData,
          activityIconPng: newActivityIcon,
        } = applyConversions(props.data, props.cmsData, conversions);

        setConvertedData(newData);
        setConvertedCmsData(newCmsData);
        setActivityIconPng(newActivityIcon);
      } catch (error) {
        console.error("Failed to convert SVG images:", sanitizeError(error));
        setConvertedData(props.data);
        setConvertedCmsData(props.cmsData);
      } finally {
        setIsConverting(false);
      }
    };

    convertImages();
  }, [props.cmsData, props.data]);

  if (isConverting) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Converting images...
      </div>
    );
  }

  return (
    <CareerPlanPDF
      {...props}
      cmsData={convertedCmsData}
      data={convertedData}
      activityIconConverted={activityIconPng}
    />
  );
};
